import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Watermark from "../components/Watermark";
import Select from "react-select";

const initialForm = {
  email: "",
  first_name: "",
  last_name: "",
  dob: "",
  employee_id: "",
  role_id: 1,
  projects: [],
};

const emailRegex = /^[a-zA-Z0-9._%+-]+@greenated\.com$/;
const empIdRegex = /^[a-zA-Z0-9]+$/;
const nameRegex = /^[A-Za-z\s]+$/;

const roleBadgeColor = (roleName) => {
  if (!roleName) return "bg-gray-100 text-gray-500";
  const r = roleName.toLowerCase();
  if (r === "superuser") return "bg-purple-100 text-purple-700";
  if (r === "admin") return "bg-blue-100 text-blue-700";
  if (r === "manager") return "bg-yellow-100 text-yellow-700";
  return "bg-green-100 text-green-700";
};

const SuperuserDashboard = () => {
  const [view, setView] = useState("users");
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectCreating, setProjectCreating] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectManagerId, setProjectManagerId] = useState("");
  const [projectMemberIds, setProjectMemberIds] = useState([]);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingProjectName, setEditingProjectName] = useState("");
  // Edit project manager/members modal
  const [editProjectModal, setEditProjectModal] = useState(null); // { id, label } or null
  const [editProjectManagerId, setEditProjectManagerId] = useState("");
  const [editProjectMemberIds, setEditProjectMemberIds] = useState([]);
  const [editProjectName, setEditProjectName] = useState("");
  const [editProjectSaving, setEditProjectSaving] = useState(false);
  // User lists for project forms (managers and employees)
  const [managerUsers, setManagerUsers] = useState([]);
  const [createMemberSearch, setCreateMemberSearch] = useState("");
  const [editMemberSearch,   setEditMemberSearch]   = useState("");
  const [employeeUsers, setEmployeeUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectMembers, setProjectMembers] = useState({ managers: [], employees: [] });
  const [membersLoading, setMembersLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("");

  const showProjects = form.role_id === 1 || form.role_id === 4;

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("jwt");

  useEffect(() => {
    fetchUsers();
    fetchProjects();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load manager and employee lists for project forms
  useEffect(() => {
    if (view !== "projects") return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${apiUrl}/users?role=Manager`, { headers }).then(r => setManagerUsers(r.data || [])).catch(() => {});
    axios.get(`${apiUrl}/users?role=employee`, { headers }).then(r => setEmployeeUsers(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data);
    } catch {
      toast.error("Failed to fetch users");
    }
    setLoading(false);
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${apiUrl}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const projectsWithManagerStatus = await Promise.all(
        res.data.map(async (p) => {
          try {
            const membersRes = await axios.get(`${apiUrl}/projects/${p.id}/members`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            return {
              value: p.id,
              label: p.project_name,
              hasManager: (membersRes.data?.managers?.length || 0) > 0,
            };
          } catch {
            return {
              value: p.id,
              label: p.project_name,
              hasManager: false,
            };
          }
        })
      );

      setProjects(projectsWithManagerStatus);
    } catch {
      toast.error("Failed to fetch projects");
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${apiUrl}/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoles(res.data);
    } catch {
      toast.error("Failed to fetch roles");
    }
  };

  const validate = () => {
    if (!form.email || !form.first_name || !form.last_name || !form.dob || !form.employee_id || !form.role_id) {
      toast.error("All fields are required"); return false;
    }
    if (!nameRegex.test(form.first_name) || !nameRegex.test(form.last_name)) {
      toast.error("Names must contain only letters and spaces"); return false;
    }
    if (!emailRegex.test(form.email)) {
      toast.error("Email must be @greenated.com"); return false;
    }
    if (!empIdRegex.test(form.employee_id)) {
      toast.error("Employee ID must be alphanumeric"); return false;
    }
    if (new Date(form.dob) > new Date()) {
      toast.error("Date of Birth cannot be in the future"); return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${apiUrl}/users/${editingId}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("User updated!");
        await fetchUsers();
      } else {
        await axios.post(`${apiUrl}/users`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("User created!");
        await fetchUsers();
      }
      await fetchProjects();
      setForm(initialForm);
      setEditingId(null);
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error saving user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setForm({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      dob: user.dob ? user.dob.split("T")[0] : "",
      employee_id: user.employee_id,
      role_id: user.role_id,
      projects: user.projects || [],
    });
    setEditingId(user.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const u = users.find(u => u.id === id);
    if (u?.role === "superuser" || u?.role_id === 3) {
      toast.error("Cannot delete a superuser"); return;
    }
    if (!window.confirm("Delete this user?")) return;
    try {
      await axios.delete(`${apiUrl}/users/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User deleted!");
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.error || "Error deleting user");
    }
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) { toast.error("Project name required"); return; }
    setProjectCreating(true);
    try {
      const trimmedName = projectName.trim();
      await axios.post(`${apiUrl}/projects`, {
        project_name: trimmedName,
        manager_id: projectManagerId ? Number(projectManagerId) : undefined,
        member_ids: projectMemberIds.map(Number),
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Refresh the list first so it's ready when the modal closes
      await fetchProjects();
      setProjectName("");
      setProjectManagerId("");
      setProjectMemberIds([]);
      setCreateMemberSearch("");
      setShowProjectModal(false);
      toast.success(`"${trimmedName}" created successfully!`, { duration: 4000 });
    } catch (err) {
      toast.error(err.response?.data?.error || "Error creating project");
    } finally {
      setProjectCreating(false);
    }
  };

  const handleViewProject = async (p) => {
    setSelectedProject(p);
    setMembersLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/projects/${p.value}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjectMembers(res.data);
    } catch {
      toast.error("Failed to load project members");
      setProjectMembers({ managers: [], employees: [] });
    } finally {
      setMembersLoading(false);
    }
  };

  const handleEditProjectSave = async () => {
    if (!editProjectName.trim()) { toast.error("Project name required"); return; }
    setEditProjectSaving(true);
    try {
      await axios.put(`${apiUrl}/projects/${editProjectModal.value}`, {
        project_name: editProjectName,
        manager_id: editProjectManagerId ? Number(editProjectManagerId) : null,
        member_ids: editProjectMemberIds.map(Number),
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Capture id before nulling modal state
      const savedProjectId = editProjectModal.value;
      const savedProjectName = editProjectName;
      setEditProjectModal(null); setEditMemberSearch("");
      toast.success("Project updated successfully!");
      // Refresh member view if this project is currently selected
      if (selectedProject?.value === savedProjectId) {
        const res = await axios.get(`${apiUrl}/projects/${savedProjectId}/members`, { headers: { Authorization: `Bearer ${token}` } });
        setProjectMembers(res.data);
        setSelectedProject(p => ({ ...p, label: savedProjectName }));
      }
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error updating project");
    } finally {
      setEditProjectSaving(false);
    }
  };

  const handleRenameProject = async (id) => {
    if (!editingProjectName.trim()) { toast.error("Project name required"); return; }
    try {
      await axios.put(`${apiUrl}/projects/${id}`, { project_name: editingProjectName }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Project renamed!");
      setProjects(prev => prev.map(p => p.value === id ? { ...p, label: editingProjectName } : p));
      setEditingProjectId(null);
      setEditingProjectName("");
    } catch (err) {
      toast.error(err.response?.data?.error || "Error renaming project");
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      await axios.delete(`${apiUrl}/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Project deleted!");
      setProjects(prev => prev.filter(p => p.value !== id));
    } catch (err) {
      toast.error(err.response?.data?.error || "Error deleting project");
    }
  };

  const cancelForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setShowForm(false);
  };

  // Stats
  const getRoleName = (roleId) => roles.find(r => r.id === roleId)?.name || "";
  const totalUsers = users.length;
  const totalEmployees = users.filter(u => getRoleName(u.role_id).toLowerCase() === "employee").length;
  const totalManagers = users.filter(u => getRoleName(u.role_id).toLowerCase() === "manager").length;
  const totalAdmins = users.filter(u => getRoleName(u.role_id).toLowerCase() === "admin").length;

  // Filtered users
  const filteredUsers = users
    .filter(u => !userSearch ||
      `${u.first_name} ${u.last_name} ${u.email} ${u.employee_id}`.toLowerCase().includes(userSearch.toLowerCase())
    )
    .filter(u => !userRoleFilter || String(u.role_id) === userRoleFilter);

  const filteredProjects = projects.filter(p =>
    !projectSearch || p.label.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const selectableProjects = form.role_id === 4
    ? projects.filter((project) => (form.projects || []).includes(project.value) || !project.hasManager)
    : projects;

  return (
    <div className="p-6 max-w-7xl mx-auto text-black">
      <Watermark />

      {/* Header */}
      <div className="flex items-center justify-between pb-6 mb-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-1 h-10 rounded-full bg-[#7AA33E]" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Superuser Dashboard</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage users, roles, and projects</p>
          </div>
        </div>
        <div className="flex gap-3">
          {view === "projects" && (
            <button
              onClick={() => setShowProjectModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:scale-[0.98] transition shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              New Project
            </button>
          )}
          {view === "users" && (
            <button
              onClick={() => { setForm(initialForm); setEditingId(null); setShowForm(true); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:scale-[0.98] transition shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add User
            </button>
          )}
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 bg-white rounded-2xl border border-gray-200 shadow-sm p-1.5 mb-6">
        {[
          { key: "users",    icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-4-4H4a4 4 0 00-4 4v1h5M9 11a4 4 0 100-8 4 4 0 000 8zm6 0a4 4 0 100-8 4 4 0 000 8z" /></svg>,    label: "Users"    },
          { key: "projects", icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" /></svg>, label: "Projects" },
        ].map(({ key, icon, label }) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition ${
              view === key ? "bg-[#7AA33E] text-white shadow-sm" : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {/* Stats â€” only on users view */}
      {view === "users" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Users",  value: totalUsers,     color: "bg-gray-50 border-gray-200",    text: "text-gray-900",  icon: <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-4-4H4a4 4 0 00-4 4v1h5M9 11a4 4 0 100-8 4 4 0 000 8zm6 0a4 4 0 100-8 4 4 0 000 8z" /></svg>,  iconBg: "bg-gray-100" },
            { label: "Employees",    value: totalEmployees, color: "bg-green-50 border-green-100",  text: "text-green-800", icon: <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>, iconBg: "bg-green-100" },
            { label: "Managers",     value: totalManagers,  color: "bg-yellow-50 border-yellow-100",text: "text-yellow-800",icon: <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>, iconBg: "bg-yellow-100" },
            { label: "Admins",       value: totalAdmins,    color: "bg-blue-50 border-blue-100",    text: "text-blue-800",  icon: <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>, iconBg: "bg-blue-100" },
          ].map(({ label, value, color, text, icon, iconBg }) => (
            <div key={label} className={`rounded-2xl border p-5 flex items-center gap-4 ${color}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>{icon}</div>
              <div>
                <p className={`text-2xl font-bold ${text}`}>{value}</p>
                <p className={`text-xs font-medium mt-0.5 ${text} opacity-70`}>{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Users Table */}
      {view === "users" && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-base font-bold text-gray-800">All Users</h2>
            <p className="text-xs text-gray-400 mt-0.5">{filteredUsers.length} of {users.length} user{users.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
              <input
                type="text"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder="Search name, email, ID..."
                className="rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition w-56"
              />
            </div>
            <select
              value={userRoleFilter}
              onChange={e => setUserRoleFilter(e.target.value)}
              className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
            >
              <option value="">All Roles</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>{r.name.charAt(0).toUpperCase() + r.name.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs tracking-wider">
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">Email</th>
                <th className="text-left py-3 px-4">Employee ID</th>
                <th className="text-left py-3 px-4">DOB</th>
                <th className="text-left py-3 px-4">Role</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="py-16 text-center"><div className="flex flex-col items-center gap-3 text-gray-400"><div className="w-8 h-8 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" /><span className="text-sm">Loading users...</span></div></td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan={6} className="py-16 text-center"><div className="flex flex-col items-center gap-3 text-gray-400"><svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-4-4H4a4 4 0 00-4 4v1h5M9 11a4 4 0 100-8 4 4 0 000 8zm6 0a4 4 0 100-8 4 4 0 000 8z" /></svg><span className="text-sm font-medium text-gray-500">No users found</span></div></td></tr>
              ) : (
                filteredUsers.map(user => {
                  const roleName = getRoleName(user.role_id);
                  const isSuperuser = roleName.toLowerCase() === "superuser" || user.role_id === 3;
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition">
                      <td className="py-3 px-4"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-[#e5f3d4] flex items-center justify-center text-[#7AA33E] font-bold text-xs flex-shrink-0">{`${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase()}</div><span className="font-medium text-gray-800">{user.first_name} {user.last_name}</span></div></td>
                      <td className="py-3 px-4 text-gray-500">{user.email}</td>
                      <td className="py-3 px-4 text-gray-500 font-mono text-xs">{user.employee_id}</td>
                      <td className="py-3 px-4 text-gray-500">
                        {user.dob ? new Date(user.dob).toLocaleDateString() : "â€”"}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${roleBadgeColor(roleName)}`}>
                          {roleName || "â€”"}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => handleEdit(user)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-100 transition"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>Edit</button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            disabled={isSuperuser}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                              isSuperuser
                                ? "bg-gray-50 border border-gray-200 text-gray-300 cursor-not-allowed"
                                : "bg-red-50 border border-red-100 text-red-600 hover:bg-red-100"
                            }`}
><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Projects View */}
      {view === "projects" && (
        <div className="flex flex-col gap-6">

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
                <p className="text-xs text-gray-500 font-medium mt-0.5">Total Projects</p>
              </div>
            </div>
            <div className="rounded-2xl border border-yellow-100 bg-yellow-50 p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-700">{projects.filter(p => p.hasManager).length}</p>
                <p className="text-xs text-yellow-600 font-medium mt-0.5">With Manager</p>
              </div>
            </div>
            <div className="rounded-2xl border border-orange-100 bg-orange-50 p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{projects.filter(p => !p.hasManager).length}</p>
                <p className="text-xs text-orange-500 font-medium mt-0.5">Needs Manager</p>
              </div>
            </div>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">

            {/* Panel Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5 border-b border-gray-100">
              <div>
                <h2 className="text-base font-bold text-gray-900">All Projects</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {filteredProjects.length === projects.length
                    ? `${projects.length} project${projects.length !== 1 ? "s" : ""}`
                    : `${filteredProjects.length} of ${projects.length} projects`}
                </p>
              </div>
              {/* Search */}
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                </svg>
                <input
                  type="text"
                  value={projectSearch}
                  onChange={e => setProjectSearch(e.target.value)}
                  placeholder="Search projects..."
                  className="rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition w-56"
                />
              </div>
            </div>

            {/* Grid Body */}
            <div className="p-6">
              {filteredProjects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <svg className="w-12 h-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                  </svg>
                  <p className="text-sm font-medium">No projects found</p>
                  <p className="text-xs mt-1">Try a different search term or create a new project</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredProjects.map(p => (
                    <div
                      key={p.value}
                      className={`group rounded-2xl border bg-white flex flex-col transition-all duration-200 cursor-pointer
                        ${editingProjectId === p.value
                          ? "border-[#7AA33E] ring-2 ring-[#7AA33E]/20 shadow-md"
                          : "border-gray-200 hover:border-[#7AA33E]/40 hover:shadow-lg hover:-translate-y-0.5"
                        }`}
                      onClick={() => { if (editingProjectId !== p.value) handleViewProject(p); }}
                    >
                      {editingProjectId === p.value ? (
                        /* ── Rename Mode ── */
                        <div className="flex flex-col gap-3 p-5">
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="w-4 h-4 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span className="text-xs font-semibold text-[#7AA33E] uppercase tracking-wide">Renaming</span>
                          </div>
                          <input
                            value={editingProjectName}
                            onChange={e => setEditingProjectName(e.target.value)}
                            className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition"
                            autoFocus
                            onKeyDown={e => { if (e.key === "Enter") handleRenameProject(p.value); if (e.key === "Escape") { setEditingProjectId(null); setEditingProjectName(""); } }}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={e => { e.stopPropagation(); handleRenameProject(p.value); }}
                              className="flex-1 py-2 rounded-xl bg-[#7AA33E] text-white text-xs font-semibold hover:bg-[#6a9335] transition"
                            >
                              Save
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); setEditingProjectId(null); setEditingProjectName(""); }}
                              className="flex-1 py-2 rounded-xl bg-white border border-gray-200 text-gray-600 text-xs font-semibold hover:bg-gray-50 transition"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* ── Normal Card ── */
                        <>
                          {/* Card Top */}
                          <div className="flex flex-col gap-3 p-5 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="w-11 h-11 rounded-xl bg-[#f0f7e6] flex items-center justify-center text-[#7AA33E] font-bold text-base flex-shrink-0 group-hover:bg-[#e5f3d4] transition-colors">
                                {p.label.charAt(0).toUpperCase()}
                              </div>
                              {/* Manager status badge */}
                              {p.hasManager ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-100 text-green-600 text-xs font-medium flex-shrink-0">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                                  Managed
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-100 text-orange-500 text-xs font-medium flex-shrink-0">
                                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block"></span>
                                  No manager
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-sm leading-snug">{p.label}</p>
                              <p className="text-xs text-gray-400 font-mono mt-0.5">#{p.value}</p>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="border-t border-gray-100 mx-5" />

                          {/* Card Actions */}
                          <div className="flex gap-2 p-3">
                            <button
                              onClick={e => { e.stopPropagation(); setEditingProjectId(p.value); setEditingProjectName(p.label); }}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gray-50 text-gray-600 text-xs font-semibold hover:bg-gray-100 transition border border-gray-100"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Rename
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); handleDeleteProject(p.value); }}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition border border-red-100"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Add / Edit User Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[92vh] border border-gray-100">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">{editingId ? "Edit User" : "Add New User"}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{editingId ? "Update the user's details below" : "Fill in the details to create a new user"}</p>
                </div>
              </div>
              <button
                onClick={cancelForm}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition text-xl leading-none"
              >&times;</button>
            </div>

            {/* Modal Body */}
            <form id="superuser-user-form" onSubmit={handleSubmit} className="flex flex-col gap-5 px-7 py-6 overflow-y-auto flex-1">

              {/* Email */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Email <span className="text-red-400">*</span></label>
                <input
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="name@greenated.com"
                  disabled={!!editingId}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:bg-gray-100 disabled:text-gray-400 transition"
                  required
                />
              </div>

              <div className="border-t border-gray-100" />

              {/* First + Last Name */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">First Name <span className="text-red-400">*</span></label>
                  <input
                    value={form.first_name}
                    onChange={e => setForm({ ...form, first_name: e.target.value })}
                    placeholder="First name"
                    className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Name <span className="text-red-400">*</span></label>
                  <input
                    value={form.last_name}
                    onChange={e => setForm({ ...form, last_name: e.target.value })}
                    placeholder="Last name"
                    className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                    required
                  />
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* DOB + Employee ID */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Date of Birth <span className="text-red-400">*</span></label>
                  <input
                    type="date"
                    value={form.dob}
                    onChange={e => setForm({ ...form, dob: e.target.value })}
                    max={new Date().toISOString().split("T")[0]}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Employee ID <span className="text-red-400">*</span></label>
                  <input
                    value={form.employee_id}
                    onChange={e => setForm({ ...form, employee_id: e.target.value })}
                    placeholder="e.g. EMP001"
                    className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                    required
                  />
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* Role */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Role <span className="text-red-400">*</span></label>
                <select
                  value={form.role_id}
                  onChange={e => setForm({ ...form, role_id: Number(e.target.value) })}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition appearance-none cursor-pointer"
                  required
                >
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.name.charAt(0).toUpperCase() + r.name.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Projects (conditional) */}
              {showProjects && (
                <>
                  <div className="border-t border-gray-100" />
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Projects</label>
                      <span className="text-xs text-gray-400">Optional</span>
                    </div>
                    <Select
                      isMulti
                      options={selectableProjects}
                      value={selectableProjects.filter(p => (form.projects || []).includes(p.value))}
                      onChange={selected => setForm({ ...form, projects: selected ? selected.map(s => s.value) : [] })}
                      placeholder="Select projects..."
                      closeMenuOnSelect={false}
                    />
                  </div>
                </>
              )}

            </form>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 px-7 py-5 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button
                type="submit"
                form="superuser-user-form"
                disabled={submitting}
                className="flex-1 py-3 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:bg-[#5d8230] disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm shadow-[#7AA33E]/20 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {editingId ? "Updating…" : "Creating…"}
                  </>
                ) : (
                  editingId ? "Update User" : "Create User"
                )}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={cancelForm}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-100 disabled:opacity-50 transition"
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col border border-gray-100">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#f0f7e6] flex items-center justify-center text-[#7AA33E] font-bold text-lg flex-shrink-0">
                  {selectedProject.label.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">{selectedProject.label}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-400 font-mono">#{selectedProject.value}</span>
                    {selectedProject.hasManager ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-50 border border-green-100 text-green-600 text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                        Managed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-50 border border-orange-100 text-orange-500 text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block"></span>
                        No manager
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedProject(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition text-xl leading-none flex-shrink-0"
              >&times;</button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto px-7 py-6 flex flex-col gap-6 flex-1">
              {membersLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-8 h-8 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-gray-400">Loading team members...</p>
                </div>
              ) : (
                <>
                  {/* Team Summary Strip */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-yellow-50 border border-yellow-100 px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-yellow-700 leading-none">{projectMembers.managers.length}</p>
                        <p className="text-xs text-yellow-600 mt-0.5">Manager</p>
                      </div>
                    </div>
                    <div className="rounded-xl bg-[#f4f9ec] border border-[#d6edb8] px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#e5f3d4] flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-4-4H4a4 4 0 00-4 4v1h5M9 11a4 4 0 100-8 4 4 0 000 8zm6 0a4 4 0 100-8 4 4 0 000 8z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-[#7AA33E] leading-none">{projectMembers.employees.length}</p>
                        <p className="text-xs text-[#7AA33E] mt-0.5">Employees</p>
                      </div>
                    </div>
                  </div>

                  {/* Edit Team Button */}
                  <button
                    onClick={() => {
                      const mgr = projectMembers.managers[0];
                      setEditProjectModal(selectedProject);
                      setEditProjectName(selectedProject.label);
                      setEditProjectManagerId(mgr ? String(mgr.id) : "");
                      setEditProjectMemberIds(projectMembers.employees.map(e => String(e.id)));
                    }}
                    className="w-full py-3 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:bg-[#5d8230] transition shadow-sm shadow-[#7AA33E]/20 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Team
                  </button>

                  {/* Manager Section */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Manager</h4>
                    {projectMembers.managers.length === 0 ? (
                      <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-dashed border-orange-200 bg-orange-50">
                        <svg className="w-4 h-4 text-orange-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                        <p className="text-sm text-orange-500 font-medium">No manager assigned yet</p>
                      </div>
                    ) : (() => {
                      const m = projectMembers.managers[0];
                      return (
                        <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-yellow-50 border border-yellow-100">
                          <div className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center text-yellow-800 font-bold text-sm flex-shrink-0">
                            {m.first_name.charAt(0)}{m.last_name.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{m.first_name} {m.last_name}</p>
                            <p className="text-xs text-gray-400 font-mono">{m.employee_id}</p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full bg-yellow-100 border border-yellow-200 text-yellow-700 text-xs font-semibold flex-shrink-0">Manager</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Employees Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Employees</h4>
                      <span className="text-xs text-gray-400 font-medium">{projectMembers.employees.length} member{projectMembers.employees.length !== 1 ? "s" : ""}</span>
                    </div>
                    {projectMembers.employees.length === 0 ? (
                      <div className="flex flex-col items-center py-8 rounded-xl border border-dashed border-gray-200 bg-gray-50 text-gray-400">
                        <svg className="w-8 h-8 mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-4-4H4a4 4 0 00-4 4v1h5M9 11a4 4 0 100-8 4 4 0 000 8zm6 0a4 4 0 100-8 4 4 0 000 8z" />
                        </svg>
                        <p className="text-sm font-medium">No employees assigned</p>
                        <p className="text-xs mt-0.5">Use Edit Team to add members</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {projectMembers.employees.map((e, idx) => (
                          <div key={e.id} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 hover:border-gray-200 transition">
                            <div className="w-9 h-9 rounded-full bg-[#e5f3d4] flex items-center justify-center text-[#7AA33E] font-bold text-sm flex-shrink-0">
                              {e.first_name.charAt(0)}{e.last_name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{e.first_name} {e.last_name}</p>
                              <p className="text-xs text-gray-400 font-mono">{e.employee_id}</p>
                            </div>
                            <span className="text-xs text-gray-400 font-mono flex-shrink-0">#{idx + 1}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button
                onClick={() => setSelectedProject(null)}
                className="w-full py-2.5 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-100 transition"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[92vh] border border-gray-100">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">Create New Project</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Fill in the details to set up your project</p>
                </div>
              </div>
              <button
                onClick={() => { setShowProjectModal(false); setProjectName(""); setProjectManagerId(""); setProjectMemberIds([]); setCreateMemberSearch(""); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition text-xl leading-none"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex flex-col gap-5 px-7 py-6 overflow-y-auto flex-1">

              {/* Project Name */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Project Name <span className="text-red-400 normal-case">*</span>
                </label>
                <input
                  type="text"
                  value={projectName}
                  onChange={e => setProjectName(e.target.value)}
                  placeholder="e.g. Climate Data Platform"
                  className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Project Manager */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Project Manager</label>
                  <span className="text-xs text-gray-400">Optional</span>
                </div>
                <select
                  value={projectManagerId}
                  onChange={e => setProjectManagerId(e.target.value)}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="">— No manager (assign later)</option>
                  {managerUsers.map(m => (
                    <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                  ))}
                </select>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Project Members */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Team Members</label>
                  {projectMemberIds.length > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f0f7e6] text-[#7AA33E] text-xs font-semibold">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      {projectMemberIds.length} selected
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">Optional</span>
                  )}
                </div>

                {employeeUsers.length === 0 ? (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <p className="text-xs text-amber-700 font-medium">No employees found. Add employees first.</p>
                  </div>
                ) : (
                  <>
                    {/* Search */}
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                      </svg>
                      <input
                        type="text"
                        value={createMemberSearch}
                        onChange={e => setCreateMemberSearch(e.target.value)}
                        placeholder="Search by name or employee ID..."
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                      />
                    </div>

                    {/* Member List */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto divide-y divide-gray-100">
                      {(() => {
                        const q = createMemberSearch.trim().toLowerCase();
                        const filtered = q
                          ? employeeUsers.filter(e => (e.first_name + " " + e.last_name).toLowerCase().includes(q) || (e.employee_id || "").toLowerCase().includes(q))
                          : employeeUsers;
                        if (filtered.length === 0)
                          return (
                            <div className="flex flex-col items-center py-6 text-gray-400">
                              <svg className="w-8 h-8 mb-1.5 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-4-4H4a4 4 0 00-4 4v1h5m6-10a4 4 0 100-8 4 4 0 000 8z" /></svg>
                              <p className="text-xs">No employees match your search.</p>
                            </div>
                          );
                        return filtered.map(emp => {
                          const sel = projectMemberIds.includes(String(emp.id));
                          return (
                            <label
                              key={emp.id}
                              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${sel ? "bg-[#f4f9ec]" : "bg-white hover:bg-gray-50"}`}
                            >
                              <input
                                type="checkbox"
                                checked={sel}
                                onChange={() => { const id = String(emp.id); setProjectMemberIds(prev => sel ? prev.filter(x => x !== id) : [...prev, id]); }}
                                className="w-4 h-4 accent-[#7AA33E] flex-shrink-0"
                              />
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${sel ? "bg-[#7AA33E] text-white" : "bg-green-100 text-green-700"}`}>
                                {emp.first_name[0]}{emp.last_name[0]}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{emp.first_name} {emp.last_name}</p>
                                <p className="text-xs text-gray-400">{emp.employee_id}</p>
                              </div>
                              {sel && (
                                <svg className="w-4 h-4 text-[#7AA33E] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              )}
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 px-7 py-5 border-t border-gray-100 flex-shrink-0 bg-gray-50 rounded-b-2xl">
              <button
                onClick={handleCreateProject}
                disabled={projectCreating}
                className="flex-1 py-3 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:bg-[#5d8230] disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm shadow-[#7AA33E]/20 flex items-center justify-center gap-2"
              >
                {projectCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Project"
                )}
              </button>
              <button
                onClick={() => { setShowProjectModal(false); setProjectName(""); setProjectManagerId(""); setProjectMemberIds([]); setCreateMemberSearch(""); }}
                disabled={projectCreating}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Edit Project Team Modal */}
      {editProjectModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[92vh] border border-gray-100">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">Edit Project</h3>
                  <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">{editProjectModal.label}</p>
                </div>
              </div>
              <button
                onClick={() => { setEditProjectModal(null); setEditMemberSearch(""); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition text-xl leading-none"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex flex-col gap-5 px-7 py-6 overflow-y-auto flex-1">

              {/* Project Name */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Project Name <span className="text-red-400 normal-case">*</span>
                </label>
                <input
                  type="text"
                  value={editProjectName}
                  onChange={e => setEditProjectName(e.target.value)}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Project Manager */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Project Manager</label>
                <select
                  value={editProjectManagerId}
                  onChange={e => setEditProjectManagerId(e.target.value)}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition appearance-none cursor-pointer"
                >
                  <option value="">— No manager</option>
                  {managerUsers.map(m => (
                    <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                  ))}
                </select>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Team Members */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Team Members</label>
                  {editProjectMemberIds.length > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f0f7e6] text-[#7AA33E] text-xs font-semibold">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      {editProjectMemberIds.length} selected
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">Optional</span>
                  )}
                </div>

                {employeeUsers.length === 0 ? (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <p className="text-xs text-amber-700 font-medium">No employees found.</p>
                  </div>
                ) : (
                  <>
                    {/* Search */}
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                      </svg>
                      <input
                        type="text"
                        value={editMemberSearch}
                        onChange={e => setEditMemberSearch(e.target.value)}
                        placeholder="Search by name or employee ID..."
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white transition"
                      />
                    </div>

                    {/* Member List */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto divide-y divide-gray-100">
                      {(() => {
                        const q = editMemberSearch.trim().toLowerCase();
                        const filtered = q
                          ? employeeUsers.filter(e => (e.first_name + " " + e.last_name).toLowerCase().includes(q) || (e.employee_id || "").toLowerCase().includes(q))
                          : employeeUsers;
                        if (filtered.length === 0)
                          return (
                            <div className="flex flex-col items-center py-6 text-gray-400">
                              <svg className="w-8 h-8 mb-1.5 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-4-4H4a4 4 0 00-4 4v1h5m6-10a4 4 0 100-8 4 4 0 000 8z" /></svg>
                              <p className="text-xs">No employees match your search.</p>
                            </div>
                          );
                        return filtered.map(emp => {
                          const sel = editProjectMemberIds.includes(String(emp.id));
                          return (
                            <label
                              key={emp.id}
                              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${sel ? "bg-[#f4f9ec]" : "bg-white hover:bg-gray-50"}`}
                            >
                              <input
                                type="checkbox"
                                checked={sel}
                                onChange={() => { const id = String(emp.id); setEditProjectMemberIds(prev => sel ? prev.filter(x => x !== id) : [...prev, id]); }}
                                className="w-4 h-4 accent-[#7AA33E] flex-shrink-0"
                              />
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${sel ? "bg-[#7AA33E] text-white" : "bg-green-100 text-green-700"}`}>
                                {emp.first_name[0]}{emp.last_name[0]}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{emp.first_name} {emp.last_name}</p>
                                <p className="text-xs text-gray-400">{emp.employee_id}</p>
                              </div>
                              {sel && (
                                <svg className="w-4 h-4 text-[#7AA33E] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              )}
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 px-7 py-5 border-t border-gray-100 flex-shrink-0 bg-gray-50 rounded-b-2xl">
              <button
                onClick={handleEditProjectSave}
                disabled={editProjectSaving}
                className="flex-1 py-3 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:bg-[#5d8230] disabled:opacity-50 transition shadow-sm shadow-[#7AA33E]/20"
              >
                {editProjectSaving ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={() => { setEditProjectModal(null); setEditMemberSearch(""); }}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default SuperuserDashboard;








